<script setup>
import { computed, nextTick, ref } from "vue";
import { FormModal } from "@Components/Modal";
import { FormCheckbox, FormDateTimePicker, FormInput, FormListbox, FormSwitch, FormTags } from "@Components/Form";
import { PrimaryButton, SecondaryButton } from "@Components/Button";
import { useI18n } from "vue-i18n";
import _map from "lodash/map";
import _cloneDeep from "lodash/cloneDeep";
import { useForm } from "@/form";
import ActionMessage from "@Components/ActionMessage.vue";

const props = defineProps(["generation", "templates", "statuses", "categories", "postTypes", "users"]);
const emit = defineEmits(["refresh"]);

const { t: __ } = useI18n();

const modal = ref();
const publishDatePicker = ref();

const form = useForm({
    id: props.generation.id,
    post_type: "post",
    publish_date: null,
    author_id: null,
    parent_page_id: null,
    categories: {
        automatic: {
            enabled: false,
        },
        custom: [],
    },
    tags: {
        automatic: {
            enabled: false,
        },
        custom: [],
    },
    template: null,
    status: "publish",
    rewrite_permalink: {
        enabled: false,
    },
});

const formattedUsers = computed(() => {
    let users = _cloneDeep(props.users);

    return users.map((u) => {
        u.name = u.data.display_name;
        return u;
    });
});

const show = () => {
    modal.value?.show();
};

const hide = () => {
    modal.value?.hide();
};

const publish = async () => {
    const response = await form.post("single_mode_publish");
    hide();
    emit("refresh");
};

defineExpose({ show, hide });
</script>

<template>
    <FormModal @submit="publish" ref="modal" :title="__('Publier l\'article')" max-height="80vh">
        <template #body>
            <div class="space-y-6">
                <FormListbox v-model="form.post_type" :items="postTypes" item-key="name" item-display="label" :label="__('Type de post')" required :z="1" />

                <FormInput v-if="form.post_type === 'page'" v-model="form.parent_page_id" :label="__('ID de la page parent')" :z="1" />

                <FormListbox
                    v-model="form.template"
                    :items="_map(templates, (key, value) => ({ name: value, key: key }))"
                    item-key="key"
                    item-display="name"
                    :label="__('Template de page')"
                    :z="1" />

                <div>
                    <div class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">{{ __("Date de publication") }}</div>
                    <div class="mt-2">
                        <div v-if="form.publish_date === null" class="flex items-center gap-3">
                            <div class="text-sm text-gray-900 dark:text-white">{{ __("Publier immédiatement") }}</div>
                            <SecondaryButton
                                type="button"
                                @click="
                                    form.publish_date = new Date();
                                    nextTick(() => {
                                        $refs.publishDatePicker.show();
                                    });
                                "
                                :z="1">
                                {{ __("Sélectionner une date") }}
                            </SecondaryButton>
                        </div>
                        <div v-else>
                            <FormDateTimePicker ref="publishDatePicker" v-model="form.publish_date" :label="__('Date de publication')" :z="1" />
                        </div>
                    </div>
                </div>

                <FormListbox
                    v-model="form.status"
                    :items="_map(statuses, (value, key) => ({ name: value, key: key }))"
                    item-key="key"
                    item-display="name"
                    :label="__('Statut')"
                    required
                    :z="1" />

                <FormListbox
                    :readonly="generation.persona_id"
                    :help="generation.persona_id ? __('Configuré avec le profil.') : undefined"
                    v-model="form.author_id"
                    :items="formattedUsers"
                    item-key="ID"
                    item-display="name"
                    :label="__('Auteur')"
                    :z="1" />

                <template v-if="form.post_type !== 'page'">
                    <FormSwitch
                        v-model="form.categories.automatic.enabled"
                        :label="__('Associer automatiquement avec l\'IA vos publications dans les catégories')" />

                    <div v-if="!form.categories.automatic.enabled">
                        <div class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">{{ __("Catégories") }}</div>
                        <div class="max-h-64 overflow-y-auto px-3 mt-2">
                            <div v-for="category in categories" :key="category.term_id" class="space-y-5">
                                <div class="relative flex items-start">
                                    <div class="h-6 flex items-center">
                                        <FormCheckbox
                                            :modelValue="form.categories.custom.includes(category.term_id)"
                                            @update:modelValue="
                                                form.categories.custom.includes(category.term_id)
                                                    ? form.categories.custom.splice(form.categories.custom.indexOf(category.term_id), 1)
                                                    : form.categories.custom.push(category.term_id)
                                            "
                                            :id="'category-' + category.term_id"
                                            name="categories"
                                            :z="1" />
                                    </div>
                                    <div class="ml-3 text-sm leading-6">
                                        <label :for="'category-' + category.term_id" class="text-gray-600 dark:text-gray-200">{{ category.name }}</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <template v-if="form.post_type !== 'page'">
                        <FormSwitch v-model="form.tags.automatic.enabled" :label="__('Générer des étiquettes automatiques avec l\'IA')" :z="1" />
                    </template>

                    <FormTags v-model="form.tags.custom" :label="__('Ajouter des étiquettes personnalisées')" :additionalSplitters="[',']" :z="1" />
                </template>

                <FormSwitch v-model="form.rewrite_permalink.enabled" :label="__('Raccourcir le permalien')" :z="1" />
            </div>
        </template>
        <template #actions>
            <div class="flex justify-end items-center">
                <ActionMessage :on="form.recentlySuccessful" class="mr-3"> {{ __("Publié.") }} </ActionMessage>
                <PrimaryButton :processing="form.processing" type="submit" :z="1">
                    {{ __("Publier") }}
                </PrimaryButton>
            </div>
        </template>
    </FormModal>
</template>
